import React, { useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { UserPlus } from "@phosphor-icons/react";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogTrigger, DialogFooter,
} from "../../../components/ui/dialog";

/**
 * CreateAgentDialog — admin-only modal that POSTs to /admin/agents.
 * Generates a unique 6-char agent_code server-side. Captures bank details
 * needed for commission payouts and an admin-only notes field.
 *
 * Props:
 *   - onCreated: callback fired after a successful create (parent should refresh the list)
 */
export function CreateAgentDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", full_name: "",
    address: "", phone: "",
    bank_account_holder: "", bank_account_number: "", bank_name: "", routing_number: "",
    commission_percent: 0.5, notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/agents", { ...form, commission_percent: Number(form.commission_percent) });
      toast.success(`Agent ${r.data.full_name} created · code ${r.data.agent_code}`);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create agent");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-xl bg-[#184A30] text-white" data-testid="create-agent-btn">
          <UserPlus size={16} className="mr-2" /> Create agent
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="serif text-3xl">New agent</DialogTitle>
          <DialogDescription>A unique 6-character agent code is auto-generated.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name" req val={form.full_name} on={v => setForm({ ...form, full_name: v })} testId="new-agent-name" />
            <Field label="Email" type="email" req val={form.email} on={v => setForm({ ...form, email: v })} mono testId="new-agent-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone" val={form.phone} on={v => setForm({ ...form, phone: v })} mono testId="new-agent-phone" />
            <Field label="Initial password" type="text" req val={form.password} on={v => setForm({ ...form, password: v })} mono testId="new-agent-password" />
          </div>
          <Field label="Address" val={form.address} on={v => setForm({ ...form, address: v })} testId="new-agent-address" />
          <div className="border-t border-[#E5E0D8] pt-3 mt-3">
            <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mb-3">Bank account for commission payouts</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Account holder" val={form.bank_account_holder} on={v => setForm({ ...form, bank_account_holder: v })} testId="new-agent-bank-holder" />
              <Field label="Bank name" val={form.bank_name} on={v => setForm({ ...form, bank_name: v })} testId="new-agent-bank-name" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Account number" val={form.bank_account_number} on={v => setForm({ ...form, bank_account_number: v })} mono testId="new-agent-bank-acct" />
              <Field label="Routing / BIN" val={form.routing_number} on={v => setForm({ ...form, routing_number: v })} mono testId="new-agent-bank-routing" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Commission %" type="number" step="0.1" min="0" max="10" val={form.commission_percent} on={v => setForm({ ...form, commission_percent: v })} mono testId="new-agent-comm-pct" />
          </div>
          <Field label="Notes (admin-only)" val={form.notes} on={v => setForm({ ...form, notes: v })} testId="new-agent-notes" />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#184A30] text-white" data-testid="new-agent-submit">
              {busy ? "Creating…" : "Create agent"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, val, on, type = "text", req, mono, testId, step, min, max }) {
  return (
    <div>
      <Label>{label}{req && <span className="text-rose-600"> *</span>}</Label>
      <input
        type={type} value={val} onChange={e => on(e.target.value)}
        required={!!req} step={step} min={min} max={max}
        className={`underline-input ${mono ? "mono" : ""}`}
        data-testid={testId}
      />
    </div>
  );
}
